import React, { useState, useRef, useEffect } from 'react';
import { ALL_BENEFITS } from '@sailpoint/bva-core';
import { bvaInputs, industryOptions, jmlAutomationOptions, currencyOptions, locationOptions, rbacMaturityOptions, userProductivityOptions, adhocAutomationOptions, managerReviewerBurdenOptions } from '../libs/store.js';

const InputForm = ({ onChange, onCurrencyChange, exchangeRate, isFetchingRate, rateError }) => {
    const [inputs, setInputs] = useState(bvaInputs);
    const [showBenefitDropdown, setShowBenefitDropdown] = useState(false);
    const benefitDropdownRef = useRef(null);

    // Update input helper
    const updateInput = (key, value) => {
        const updated = { ...inputs, [key]: value };
        setInputs(updated);
        if (onChange) onChange(updated);
    };

    // Checkbox logic
    const handleCheckboxChange = (benefit, checked) => {
        let updatedBenefits = [...inputs.desired_benefits];
        if (checked) {
            if (benefit === 'All Benefits') {
                updatedBenefits = [...ALL_BENEFITS];
            } else {
                if (!updatedBenefits.includes(benefit)) updatedBenefits.push(benefit);
                if (benefit === 'Improve Internal Reviews' &&
                    !updatedBenefits.includes('AI / ML Based Access Recommendations')) {
                    updatedBenefits.push('AI / ML Based Access Recommendations');
                }
            }
        } else {
            if (benefit === 'All Benefits') {
                updatedBenefits = [];
            } else {
                updatedBenefits = updatedBenefits.filter((b) => b !== benefit);
                if (benefit === 'Improve Internal Reviews') {
                    updatedBenefits = updatedBenefits.filter(
                        (b) => b !== 'AI / ML Based Access Recommendations'
                    );
                }
            }
        }
        updatedBenefits = updatedBenefits.filter((b) => b !== 'All Benefits');
        const updatedInputs = { ...inputs, desired_benefits: updatedBenefits };
        setInputs(updatedInputs);
        onChange?.(updatedInputs);
    };

    // Select all/deselect all
    const selectAllBenefits = () => updateInput('desired_benefits', [...ALL_BENEFITS]);
    const deselectAllBenefits = () => updateInput('desired_benefits', []);

    const handleCurrencyChange = (e) => {
        const selected = currencyOptions.find((opt) => opt.isoCode === e.target.value);
        if (selected) {
            const updated = {
                ...inputs,
                currency_iso_code: selected.isoCode,
                currency_symbol: selected.symbol
            };
            setInputs(updated);
            if (onChange) onChange(updated);
            if (onCurrencyChange) onCurrencyChange(selected.isoCode);
        }
    };

    // Number input helper
    const handleNumberInput = (key) => (e) => {
        const value = e.target.valueAsNumber;
        updateInput(key, isNaN(value) ? 0 : value);
    };
    // Select input helper
    const handleSelectInput = (key) => (e) => {
        updateInput(key, e.target.value);
    };

    // Dropdown outside click
    useEffect(() => {
        function handleClickOutside(event) {
            if (showBenefitDropdown && benefitDropdownRef.current && !benefitDropdownRef.current.contains(event.target)) {
                setShowBenefitDropdown(false);
            }
        }
        document.addEventListener('click', handleClickOutside, true);
        return () => document.removeEventListener('click', handleClickOutside, true);
    }, [showBenefitDropdown]);

    const areAllBenefitsSelected = inputs.desired_benefits.length === ALL_BENEFITS.length;
    const isIndeterminate = inputs.desired_benefits.length > 0 && !areAllBenefitsSelected;

    // Styling classes
    const inputClasses = "mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm";
    const labelWithMoreInfoClasses = "block text-sm font-medium text-gray-700 cursor-help group relative z-0 inline-flex items-center gap-1 group-hover:z-50 group-focus-within:z-50";
    const labelClasses = "block text-sm font-medium text-gray-700 group relative inline-flex items-center gap-1";
    const sectionTitleClasses = "text-lg font-semibold text-gray-800 mb-4 border-b pb-2";
    const gridClasses = "grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4";
    const moreInfoText = "pointer-events-none absolute left-0 bottom-full z-50 mb-1 hidden w-56 rounded bg-gray-900 px-2 py-1 text-xs font-normal text-white group-hover:block group-focus-within:block";


    return (
        <div className="space-y-8">
            {/* General Information */}
            <section>
                <h2 className={sectionTitleClasses}>General Information</h2>
                <p className="text-sm text-gray-500 mb-0">All monetary values should be entered in USD, regardless of Currency selected.</p>
                <p className="text-sm text-gray-500 mb-4">When a different currency is selected, the results are automatically converted using the latest exchange rate.</p>
                <div className={gridClasses}>
                    <div>
                        <label htmlFor="total-users" className={labelWithMoreInfoClasses}>
                            Total Users
                            <span className={moreInfoText}>
                                Total number of internal employees, and external users.
                            </span>
                        </label>
                        <input type="number" id="total-users" className={inputClasses} value={inputs.total_users} onChange={handleNumberInput('total_users')} min={0} />
                    </div>
                    <div>
                        <label htmlFor="avg-salary" className={labelWithMoreInfoClasses}>
                            Average Annual Salary
                            <span className={moreInfoText}>
                                The typical yearly pay earned by an employee, and externals.
                            </span>
                        </label>
                        <input type="number" id="avg-salary" className={inputClasses} value={inputs.average_annual_salary} onChange={handleNumberInput('average_annual_salary')} min={0} />
                    </div>
                    <div>
                        <label htmlFor="avg-manager-salary" className={labelWithMoreInfoClasses}>
                            Average Manager Salary
                            <span className={moreInfoText}>
                                The typical yearly pay earned by a managerial position in the company.
                            </span>
                        </label>
                        <input type="number" id="avg-manager-salary" className={inputClasses} value={inputs.average_manager_salary} onChange={handleNumberInput('average_manager_salary')} min={0} />
                    </div>
                    <div>
                        <label htmlFor="avg-helpdesk-salary" className={labelWithMoreInfoClasses}>
                            Average IT / Helpdesk Salary
                            <span className={moreInfoText}>
                                The typical yearly pay earned by IT/helpdesk staff involved in access request fulfillment.
                            </span>
                        </label>
                        <input type="number" id="avg-helpdesk-salary" className={inputClasses} value={inputs.average_it_helpdesk_salary} onChange={handleNumberInput('average_it_helpdesk_salary')} min={0} />
                    </div>
                    <div>
                        <label htmlFor="salary-burden" className={labelWithMoreInfoClasses}>
                            Salary Burden (%)
                            <span className={moreInfoText}>
                                The percentage of total salary that goes toward employee benefits.
                            </span>
                        </label>
                        <input type="number" id="salary-burden" className={inputClasses} value={inputs.salary_burden_pc} onChange={handleNumberInput('salary_burden_pc')} min={0} max={100} />
                    </div>
                    <div>
                        <label htmlFor="location" className={labelWithMoreInfoClasses}>
                            Location
                            <span className={moreInfoText}>
                                The geographic place where the job/company is based.
                            </span>
                        </label>
                        <select id="location" className={inputClasses} value={inputs.location} onChange={handleSelectInput('location')}>
                            {locationOptions.map((loc) => <option key={loc} value={loc}>{loc}</option>)}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="industry" className={labelWithMoreInfoClasses}>
                            Industry
                            <span className={moreInfoText}>
                                The sector of the economy in which the company operates.
                            </span>
                        </label>
                        <select id="industry" className={inputClasses} value={inputs.industry} onChange={handleSelectInput('industry')}>
                            {industryOptions.map((ind) => <option key={ind} value={ind}>{ind}</option>)}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="currency" className={labelWithMoreInfoClasses}>
                            Currency
                            <span className={moreInfoText}>
                                The type of money in which salaries and costs are denominated.
                            </span>
                        </label>
                        <select id="currency" className={inputClasses} value={inputs.currency_iso_code} onChange={handleCurrencyChange} disabled={isFetchingRate}>
                            {currencyOptions.map((curr) => <option key={curr.isoCode} value={curr.isoCode}>{curr.name}</option>)}
                        </select>
                        {isFetchingRate && (
                            <p className="text-xs text-blue-600 mt-1">Fetching exchange rate...</p>
                        )}
                        {rateError && (
                            <p className="text-xs text-amber-600 mt-1">{rateError}</p>
                        )}
                        {!isFetchingRate && !rateError && inputs.currency_iso_code !== 'USD' && (
                            <p className="text-xs text-gray-500 mt-1">1 USD = {exchangeRate} {inputs.currency_iso_code}</p>
                        )}
                    </div>
                </div>
            </section>
            {/* Desired Benefits Dropdown */}
            <section>
                <h2 className={sectionTitleClasses}>Desired Benefits</h2>
                <div className="relative" ref={benefitDropdownRef}>
                    <label id="benefit-label" className={labelClasses + ' mb-1'}>Select Benefits</label>
                    <button
                        type="button"
                        aria-haspopup="listbox"
                        aria-labelledby="benefit-label"
                        onClick={() => setShowBenefitDropdown((v) => !v)}
                        className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
                    >
                        <span className="block truncate">
                            {inputs.desired_benefits.length === 0
                                ? 'No benefits selected'
                                : areAllBenefitsSelected
                                    ? 'All benefits selected'
                                    : `${inputs.desired_benefits.length} benefit(s) selected`}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.24a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z" clipRule="evenodd" />
                            </svg>
                        </span>
                    </button>
                    {showBenefitDropdown && (
                        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                            <div className="px-3 py-2 border-b flex items-center">
                                <input
                                    type="checkbox"
                                    checked={areAllBenefitsSelected}
                                    ref={el => { if (el) el.indeterminate = isIndeterminate; }}
                                    onChange={e => (e.target.checked ? selectAllBenefits() : deselectAllBenefits())}
                                    className="mr-2"
                                />
                                <span className="text-xs">Select All</span>
                                <button type="button" className="ml-auto text-xs text-blue-600 hover:underline" onClick={deselectAllBenefits}>Clear</button>
                            </div>
                            {ALL_BENEFITS.map((benefit) => (
                                <label key={benefit} className="flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50">
                                    <input
                                        type="checkbox"
                                        checked={inputs.desired_benefits.includes(benefit)}
                                        onChange={e => handleCheckboxChange(benefit, e.target.checked)}
                                        className="mr-2"
                                    />
                                    <span className="text-xs">{benefit}</span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Benefit Realization (%) Section */}
            <section>
                <h2 className={sectionTitleClasses}>Benefit Realization (%)</h2>
                <div className={gridClasses}>
                    {[1, 2, 3, 4, 5].map((year) => {
                        const inputKey = `year_${year}_realized_benefit`;
                        return (
                            <div key={year}>
                                <label htmlFor={`realization-y${year}`} className={labelClasses}>
                                    Year {year} Realized Benefit (%)
                                </label>
                                <input
                                    type="number"
                                    id={`realization-y${year}`}
                                    className={inputClasses}
                                    value={inputs[inputKey] || ''}
                                    onChange={handleNumberInput(inputKey)}
                                    min={0}
                                    max={100}
                                />
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Detailed Assumptions Section */}
            <section>
                <h2 className={sectionTitleClasses}>Detailed Assumptions</h2>
                <div className="space-y-6">
                    {inputs.desired_benefits.length === 0 && (
                        <div className="text-center py-6 px-4 bg-gray-50 rounded-md border border-gray-200">
                            <p className="text-gray-500 italic">
                                Please select one or more options from the "Desired Benefits" section above to see the relevant detailed assumptions.
                            </p>
                        </div>
                    )}

                    {/* Optimize SaaS Management */}
                    {inputs.desired_benefits.includes('Optimize SaaS Management') && (
                        <div>
                            <h3 className={"text-md font-semibold text-gray-700 mt-4 mb-2 col-span-1 md:col-span-2"}>Optimize SaaS Management</h3>
                            <div className={gridClasses}>
                                <div>
                                    <label htmlFor="avg-saas-apps" className={labelWithMoreInfoClasses}>
                                        Avg. SaaS Apps per User
                                        <span className={moreInfoText}>
                                            The typical number of SaaS Apps provisioned to a user.
                                        </span>
                                    </label>
                                    <span className={moreInfoText}>
                                        The typical number of SaaS Apps provisioned to a user.
                                    </span>
                                    <input type="number" id="avg-saas-apps" className={inputClasses} value={inputs.avg_saas_apps || ''} onChange={handleNumberInput('avg_saas_apps')} min={0} />
                                </div>
                                <div>
                                    <label htmlFor="avg-saas-cost" className={labelWithMoreInfoClasses}>Average Annual SaaS App Cost per User (USD)
                                        <span className={moreInfoText}>
                                            The typical cost of a SaaS Application per user.
                                        </span>
                                    </label>
                                    <input type="number" id="avg-saas-cost" className={inputClasses} value={inputs.avg_saas_app_cost || ''} onChange={handleNumberInput('avg_saas_app_cost')} min={0} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Oversee Multi-Cloud Environments */}
                    {inputs.desired_benefits.includes('Oversee Multi-Cloud Environments') && (
                        <div>
                            <h3 className={"text-md font-semibold text-gray-700 mt-4 mb-2 col-span-1 md:col-span-2"}>Oversee Multi-Cloud Environments</h3>
                            <div className={gridClasses}>
                                <div>
                                    <label htmlFor="cloud-admins" className={labelWithMoreInfoClasses}>Number of Cloud Administrators (FTEs)
                                        <span className={moreInfoText}>
                                            The number of Cloud Administrators focusing on identity related activities across cloud environments.
                                        </span>
                                    </label>
                                    <input type="number" id="cloud-admins" className={inputClasses} value={inputs.no_cloud_admins || ''} onChange={handleNumberInput('no_cloud_admins')} min={0} step={0.1} />
                                    <p className="text-xs text-gray-500 mt-1 col-span-1 md:col-span-2">Benchmark: Manual process requires ~1 FTE per 5000 high-access employees.</p>
                                </div>
                                <div>
                                    <label htmlFor="cloud-envs" className={labelWithMoreInfoClasses}>Number of Public Cloud Environments (AWS, Azure, GCP)
                                        <span className={moreInfoText}>
                                            The number of public cloud environments (AWS, GCP, Azure) you utilize.
                                        </span>
                                    </label>
                                    <input type="number" id="cloud-envs" className={inputClasses} value={inputs.number_of_public_cloud_environments || ''} onChange={handleNumberInput('number_of_public_cloud_environments')} min={0} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Manage ERP Access Risk */}
                    {inputs.desired_benefits.includes('Manage ERP Access Risk') && (
                        <div>
                            <h3 className={"text-md font-semibold text-gray-700 mt-4 mb-2 col-span-1 md:col-span-2"}>Manage ERP Access Risk</h3>
                            <div className={gridClasses}>
                                <div>
                                    <label htmlFor="erp-access-hours" className={labelClasses}>Hours to Implement/Revamp SOD Controls</label>
                                    <input type="number" id="erp-access-hours" className={inputClasses} value={inputs.input_erp_access || ''} onChange={handleNumberInput('input_erp_access')} min={0} />
                                    <p className="text-xs text-gray-500 mt-1 col-span-1 md:col-span-2">Benchmark: Manual process requires ~40 hours per 1000 employees.</p>
                                </div>
                                <div>
                                    <label htmlFor="erp-resources" className={labelClasses}>Resources Maintaining SOD/GRC (FTEs)</label>
                                    <input type="number" id="erp-resources" className={inputClasses} value={inputs.input_erp_resources || ''} onChange={handleNumberInput('input_erp_resources')} min={0} step={0.1} />
                                    <p className="text-xs text-gray-500 mt-1 col-span-1 md:col-span-2">Benchmark: Manual process requires ~0.1 FTE per 1000 employees.</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Automate Lifecycle Events */}
                    {inputs.desired_benefits.includes('Automate Lifecycle Events') && (
                        <div>
                            <h3 className={"text-md font-semibold text-gray-700 mt-4 mb-2 col-span-1 md:col-span-2"}>Automate Lifecycle Events (JML)</h3>
                            <div className={gridClasses}>
                                <div>
                                    <label htmlFor="pc-joiners" className={labelWithMoreInfoClasses}>% Joiners (Annual)
                                        <span className={moreInfoText}>
                                            The % of joiners (new hires) being onboarded annually.
                                        </span>
                                    </label>
                                    <input type="number" id="pc-joiners" className={inputClasses} value={inputs.pc_joiners || ''} onChange={handleNumberInput('pc_joiners')} min={0} max={100} />
                                </div>
                                <div>
                                    <label htmlFor="pc-movers" className={labelWithMoreInfoClasses}>% Movers (Annual)
                                        <span className={moreInfoText}>
                                            The % of movers (promotions, role changes) being managed annually.
                                        </span>
                                    </label>
                                    <input type="number" id="pc-movers" className={inputClasses} value={inputs.pc_movers || ''} onChange={handleNumberInput('pc_movers')} min={0} max={100} />
                                </div>
                                <div>
                                    <label htmlFor="pc-leavers" className={labelWithMoreInfoClasses}>% Leavers (Annual)
                                        <span className={moreInfoText}>
                                            The % of leavers (terminations) being managed annually.
                                        </span>
                                    </label>
                                    <input type="number" id="pc-leavers" className={inputClasses} value={inputs.pc_leavers || ''} onChange={handleNumberInput('pc_leavers')} min={0} max={100} />
                                </div>
                                <div>
                                    <label htmlFor="jml-automation" className={labelWithMoreInfoClasses}>Current Level of JML Automation
                                        <span className={moreInfoText}>
                                            The average execution time spent by IT (Helpdesk, App Owner, Security, etc.) spend enabling a user ad hoc request.
                                        </span>
                                    </label>
                                    <select id="jml-automation" className={inputClasses} value={inputs.current_level_of_jml_automation || ''} onChange={handleSelectInput('current_level_of_jml_automation')}>
                                        {jmlAutomationOptions.map((ind) => <option key={ind} value={ind}>{ind}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Expedite Ad Hoc Requests */}
                    {inputs.desired_benefits.includes('Expedite Ad Hoc Requests') && (
                        <div>
                            <h3 className={"text-md font-semibold text-gray-700 mt-4 mb-2 col-span-1 md:col-span-2"}>Expedite Ad-Hoc Requests</h3>
                            <div className={gridClasses}>
                                <div>
                                    <label htmlFor="adhoc-per-user" className={labelWithMoreInfoClasses}>Annual Ad-Hoc Requests per User
                                        <span className={moreInfoText}>
                                            The number of user access requests that are being processed per user annually outside of joiner, and mover events.
                                        </span>
                                    </label>
                                    <input type="number" id="adhoc-per-user" className={inputClasses} value={inputs.annual_adhoc_requests_per_user || ''} onChange={handleNumberInput('annual_adhoc_requests_per_user')} min={0} />
                                </div>
                                <div>
                                    <label htmlFor="adhoc-automation" className={labelWithMoreInfoClasses}>Current Level of Ad-Hoc Request Automation
                                        <span className={moreInfoText}>
                                            The average execution time spent by IT (Helpdesk, App Owner, Security, etc.) enabling a user ad hoc request.
                                        </span>
                                    </label>
                                    <select id="adhoc-automation" className={inputClasses} value={inputs.current_level_of_adhoc_access_request_automation || ''} onChange={handleSelectInput('current_level_of_adhoc_access_request_automation')}>
                                        {adhocAutomationOptions.map((ind) => <option key={ind} value={ind}>{ind}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Reduce Password Resets */}
                    {inputs.desired_benefits.includes('Reduce Password Resets') && (
                        <div>
                            <h3 className={"text-md font-semibold text-gray-700 mt-4 mb-2 col-span-1 md:col-span-2"}>Reduce Password Resets</h3>
                            <div className={gridClasses}>
                                <div className="md:col-span-1">
                                    <label htmlFor="password-resets" className={labelWithMoreInfoClasses}>Annual Password Resets per User
                                        <span className={moreInfoText}>
                                            The number password reset requests that are being processed per user annually.
                                        </span>
                                    </label>
                                    <input type="number" id="password-resets" className={inputClasses} value={inputs.annual_password_resets_per_user || ''} onChange={handleNumberInput('annual_password_resets_per_user')} min={0} />
                                </div>
                                <div className="hidden md:block"></div>
                            </div>
                        </div>
                    )}

                    {/* Optimize User Productivity */}
                    {(inputs.desired_benefits.includes('Automate Lifecycle Events') || inputs.desired_benefits.includes('Expedite Ad Hoc Requests') || inputs.desired_benefits.includes('Reduce Password Resets')) && (
                        <div>
                            <h3 className={"text-md font-semibold text-gray-700 mt-4 mb-2 col-span-1 md:col-span-2"}>Optimize User Productivity</h3>
                            <div className={gridClasses}>
                                <div className="md:col-span-2">
                                    <label htmlFor="user-productivity" className={labelWithMoreInfoClasses}>How much time is lost due to users waiting for access?
                                        <span className={moreInfoText}>
                                            The average time that a user spends waiting for access to applications they need to do their jobs e.g., if a joiner starts on Monday and does not have access until Wednesday, that's 16 hours (2 days). If a joiner starts on Monday and has access when they start, that's 0 hours (despite that it may have taken 2-3 days in the week BEFORE the joiner started).
                                        </span>
                                    </label>
                                    <select id="user-productivity" className={inputClasses} value={inputs.user_productivity || ''} onChange={handleSelectInput('user_productivity')}>
                                        {userProductivityOptions.map((ind) => <option key={ind} value={ind}>{ind}</option>)}
                                    </select>
                                    <p className="text-xs text-gray-500 mt-1 col-span-1 md:col-span-2">Savings calculated based on improvements selected for JML, Ad-Hoc Requests, and Password Resets.</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Improve Internal Reviews / AI / ML Based Access Recommendations */}
                    {(inputs.desired_benefits.includes('Improve Internal Reviews') || inputs.desired_benefits.includes('AI / ML Based Access Recommendations')) && (
                        <div>
                            <h3 className={"text-md font-semibold text-gray-700 mt-4 mb-2 col-span-1 md:col-span-2"}>
                                Improve Internal Reviews {inputs.desired_benefits.includes('AI / ML Based Access Recommendations') ? '(with AI/ML)' : ''}
                            </h3>
                            <div className={gridClasses}>
                                <div>
                                    <label htmlFor="compliance-analysts" className={labelWithMoreInfoClasses}>Number of Compliance Analysts (FTEs)
                                        <span className={moreInfoText}>
                                            Number of full-time employees that run access reviews (campaigns, contacting managers, reporting).
                                        </span>
                                    </label>
                                    <input type="number" id="compliance-analysts" className={inputClasses} value={inputs.number_of_compliance_analysts || ''} onChange={handleNumberInput('number_of_compliance_analysts')} min={0} step={0.1} />
                                    <p className="text-xs text-gray-500 mt-1 col-span-1 md:col-span-2">Benchmark: Manual process requires ~1 FTE per 2000 employees.</p>
                                </div>
                                <div>
                                    <label htmlFor="manager-burden" className={labelWithMoreInfoClasses}>Level of Manager Reviewer Burden (Access Cert.)
                                        <span className={moreInfoText}>
                                            The average time that a manager or app owner spends reviewing each direct report's access.
                                        </span>
                                    </label>
                                    <select id="manager-burden" className={inputClasses} value={inputs.level_of_manager_reviewer_burden_during_access_certification || ''} onChange={handleSelectInput('level_of_manager_reviewer_burden_during_access_certification')}>
                                        {managerReviewerBurdenOptions.map((ind) => <option key={ind} value={ind}>{ind}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Avoid Audit Findings */}
                    {inputs.desired_benefits.includes('Avoid Audit Findings') && (
                        <div>
                            <h3 className={"text-md font-semibold text-gray-700 mt-4 mb-2 col-span-1 md:col-span-2"}>Avoid Audit Findings</h3>
                            <div className={gridClasses}>
                                <div>
                                    <label htmlFor="cost-per-finding" className={labelWithMoreInfoClasses}>Average Cost per Audit Finding (USD)
                                        <span className={moreInfoText}>
                                            The cost of providing data to auditors and remediating each audit gap. Source: Forrester "Total Economic Impact of IGA".
                                        </span>
                                    </label>
                                    <input type="number" id="cost-per-finding" className={inputClasses} value={inputs.average_cost_per_audit_finding || ''} onChange={handleNumberInput('average_cost_per_audit_finding')} min={0} />
                                </div>
                                <div>
                                    <label htmlFor="annual-findings" className={labelWithMoreInfoClasses}>Annual Identity-Related Audit Findings
                                        <span className={moreInfoText}>
                                            The average number of audit gaps that your external auditors identify annually over the last 3 years. Source: Forrester "Total Economic Impact of IGA".
                                        </span>
                                    </label>
                                    <input type="number" id="annual-findings" className={inputClasses} value={inputs.annual_audit_findings || ''} onChange={handleNumberInput('annual_audit_findings')} min={0} />
                                    <p className="text-xs text-gray-500 mt-1 col-span-1 md:col-span-2">Benchmark: Manual process yields ~0.5 findings per 1000 employees.</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Replace Legacy Systems */}
                    {inputs.desired_benefits.includes('Replace Legacy Systems') && (
                        <div>
                            <h3 className={"text-md font-semibold text-gray-700 mt-4 mb-2 col-span-1 md:col-span-2"}>Replace Legacy Systems</h3>
                            <div className={gridClasses}>
                                <div className="md:col-span-2">
                                    <label htmlFor="legacy-costs" className={labelWithMoreInfoClasses}>Annual Legacy Systems Costs (USD)
                                        <span className={moreInfoText}>
                                            The annual total costs to operate your existing system that can potentially be replaced by SailPoint (personnel, subscription, maintenance, upgrades, hardware, etc.).
                                        </span>
                                    </label>
                                    <input type="number" id="legacy-costs" className={inputClasses} value={inputs.annual_legacy_systems_costs || ''} onChange={handleNumberInput('annual_legacy_systems_costs')} min={0} />
                                    <p className="text-xs text-gray-500 mt-1 col-span-1 md:col-span-2">Include licenses, maintenance, infrastructure, personnel, testing, etc., that will be eliminated.</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Accelerate Application Onboarding */}
                    {inputs.desired_benefits.includes('Accelerate Application Onboarding') && (
                        <div>
                            <h3 className={"text-md font-semibold text-gray-700 mt-4 mb-2 col-span-1 md:col-span-2"}>Accelerate Application Onboarding</h3>
                            <div className={gridClasses}>
                                <div className="md:col-span-1">
                                    <label htmlFor="apps-onboarded" className={labelClasses}>Applications to be Onboarded (Annual Avg)</label>
                                    <input type="number" id="apps-onboarded" className={inputClasses} value={inputs.applications_to_be_onboarded || ''} onChange={handleNumberInput('applications_to_be_onboarded')} min={0} />
                                </div>
                                <div className="hidden md:block"></div>
                            </div>
                        </div>
                    )}

                    {/* Govern Machine Accounts */}
                    {inputs.desired_benefits.includes('Govern Machine Accounts') && (
                        <div>
                            <h3 className={"text-md font-semibold text-gray-700 mt-4 mb-2 col-span-1 md:col-span-2"}>Govern Machine Accounts</h3>
                            <div className={gridClasses}>
                                <div className="md:col-span-2">
                                    <label htmlFor="machine-accounts" className={labelClasses}>How many machine accounts are in focus?</label>
                                    <input type="number" id="machine-accounts" className={inputClasses} value={inputs.machine_accounts_in_focus || ''} onChange={handleNumberInput('machine_accounts_in_focus')} min={0} />
                                </div>
                                <div className="md:col-span-2">
                                    <label htmlFor="machine-requests" className={labelClasses}>How many requests do you receive for machine account access per year (creation, deletion, updates, etc.)?</label>
                                    <input type="number" id="machine-requests" className={inputClasses} value={inputs.machine_account_requests_per_year || ''} onChange={handleNumberInput('machine_account_requests_per_year')} min={0} />
                                </div>
                                <div className="md:col-span-2">
                                    <label htmlFor="review-machine-accounts" className={labelClasses}>How many minutes does it take to perform access reviews per machine account?</label>
                                    <input type="number" id="review-machine-accounts" className={inputClasses} value={inputs.machine_account_review_time_mins || ''} onChange={handleNumberInput('machine_account_review_time_mins')} min={0} />
                                </div>
                                <div className="md:col-span-2">
                                    <label htmlFor="govern-machine-accounts" className={labelClasses}>How many minutes does it take for an analyst to govern (discover, inventory, assign ownership, report) each machine account?</label>
                                    <input type="number" id="govern-machine-accounts" className={inputClasses} value={inputs.machine_account_govern_time_mins || ''} onChange={handleNumberInput('machine_account_govern_time_mins')} min={0} />
                                </div>

                            </div>
                        </div>
                    )}

                    {/* Govern Agent Identity Security */}
                    {inputs.desired_benefits.includes('Govern Agent Identity Security') && (
                        <div>
                            <h3 className={"text-md font-semibold text-gray-700 mt-4 mb-2 col-span-1 md:col-span-2"}>Govern Agent Identity Security</h3>
                            <div className={gridClasses}>
                                <div>
                                    <label htmlFor="ai-agents" className={labelClasses}>Total number of AI Agents currently deployed across the enterprise (managed or unmanaged outside SailPoint)</label>
                                    <input type="number" id="ai-agents" className={inputClasses} value={inputs.agent_identities_in_focus || ''} onChange={handleNumberInput('agent_identities_in_focus')} min={0} />
                                    <p className="text-xs text-gray-500 mt-1">Default: 1x of total users (auto-calculated, can be overridden)</p>
                                </div>
                                <div>
                                    <label htmlFor="govern-ai-agents" className={labelClasses}>How long, on avg., does it take for an analyst to govern (discover, inventory, assign ownership, report) each AI Agent?</label>
                                    <input type="number" id="govern-ai-agents" className={inputClasses} value={inputs.agent_identity_govern_time_mins || ''} onChange={handleNumberInput('agent_identity_govern_time_mins')} min={0} />
                                    <p className="text-xs text-gray-500 mt-1">Time in minutes (default: 15)</p>
                                </div>
                                <div>
                                    <label htmlFor="ai-agent-change-requests" className={labelClasses}>How many change requests do you receive for AI Agents access per year (updates, deletion, etc.)?</label>
                                    <input type="number" id="ai-agent-change-requests" className={inputClasses} value={inputs.agent_identity_change_requests_per_year || ''} onChange={handleNumberInput('agent_identity_change_requests_per_year')} min={0} />
                                    <p className="text-xs text-gray-500 mt-1">Default: 0.1x of total users (auto-calculated, can be overridden)</p>
                                </div>
                                <div>
                                    <label htmlFor="new-ai-agents" className={labelClasses}>How many new AI Agents do you plan to create and deploy every year?</label>
                                    <input type="number" id="new-ai-agents" className={inputClasses} value={inputs.agent_identity_new_agents_per_year || ''} onChange={handleNumberInput('agent_identity_new_agents_per_year')} min={0} />
                                    <p className="text-xs text-gray-500 mt-1">Default: 0.1x of total users (auto-calculated, can be overridden)</p>
                                </div>
                                <div>
                                    <label htmlFor="review-ai-agents" className={labelClasses}>How long does it take to perform access reviews per AI Agent?</label>
                                    <input type="number" id="review-ai-agents" className={inputClasses} value={inputs.agent_identity_review_time_mins || ''} onChange={handleNumberInput('agent_identity_review_time_mins')} min={0} />
                                    <p className="text-xs text-gray-500 mt-1">Time in minutes (default: 15)</p>
                                </div>
                                <div>
                                    <label htmlFor="revenue-ai-agents" className={labelClasses}>What % of the AI Agents are revenue generating?</label>
                                    <input type="number" id="revenue-ai-agents" className={inputClasses} value={inputs.agent_identities_revenue_generating_pc || ''} onChange={handleNumberInput('agent_identities_revenue_generating_pc')} min={0} max={100} />
                                    <p className="text-xs text-gray-500 mt-1">Percentage (default: 10%)</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Streamline Role-Based Access */}
                    {inputs.desired_benefits.includes('Streamline Role-Based Access') && (
                        <div>
                            <h3 className={"text-md font-semibold text-gray-700 mt-4 mb-2 col-span-1 md:col-span-2"}>Streamline Role-Based Access</h3>
                            <div className={gridClasses}>
                                <div>
                                    <label htmlFor="role-ftes" className={labelWithMoreInfoClasses}>Number of FTEs Focused on Roles
                                        <span className={moreInfoText}>
                                            The number of resources (e.g., Role Managers) that are dedicated to maintaining existing roles
                                        </span>
                                    </label>
                                    <input type="number" id="role-ftes" className={inputClasses} value={inputs.number_of_ftes || ''} onChange={handleNumberInput('number_of_ftes')} min={0} step={0.1} />
                                    <p className="text-xs text-gray-500 mt-1 col-span-1 md:col-span-2">Benchmark: Manual process requires ~0.2 FTEs per 1000 employees.</p>
                                </div>
                                <div>
                                    <label htmlFor="rbac-maturity" className={labelWithMoreInfoClasses}>Maturity Level of RBAC System</label>
                                    <select id="rbac-maturity" className={inputClasses} value={inputs.maturity_level_of_RBAC_system || ''} onChange={handleSelectInput('maturity_level_of_RBAC_system')}>
                                        {rbacMaturityOptions.map((ind) => <option key={ind} value={ind}>{ind}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Minimize Breach Costs */}
                    {inputs.desired_benefits.includes('Minimize Breach Costs') && (
                        <div>
                            <h3 className={"text-md font-semibold text-gray-700 mt-4 mb-2 col-span-1 md:col-span-2"}>Minimize Breach Costs</h3>
                            <div className={gridClasses}>
                                <div>
                                    <label htmlFor="breach-pre" className={labelWithMoreInfoClasses}>Annual Breach Likelihood (%) Pre-SailPoint
                                        <span className={moreInfoText}>
                                            Industry benchmark. Source: "Making the Business Case for IAM".
                                        </span>
                                    </label>
                                    <input type="number" id="breach-pre" className={inputClasses} value={inputs.annual_breach_likelihood_pre_sailpoint || ''} onChange={handleNumberInput('annual_breach_likelihood_pre_sailpoint')} min={0} max={100} />
                                </div>
                                <div>
                                    <label htmlFor="breach-post" className={labelWithMoreInfoClasses}>Target Annual Breach Likelihood (%) Post-SailPoint
                                        <span className={moreInfoText}>
                                            Industry benchmark. Source: "Making the Business Case for IAM".
                                        </span>
                                    </label>
                                    <input type="number" id="breach-post" className={inputClasses} value={inputs.annual_breach_likelihood_post_sailpoint || ''} onChange={handleNumberInput('annual_breach_likelihood_post_sailpoint')} min={0} max={100} />
                                </div>
                                <p className="text-xs text-gray-500 mt-1 col-span-1 md:col-span-2">Breach cost is estimated based on selected Location and Industry.</p>
                            </div>
                        </div>
                    )}

                    {/* Govern Unstructured Data */}
                    {inputs.desired_benefits.includes('Govern Unstructured Data') && (
                        <div>
                            <h3 className={"text-md font-semibold text-gray-700 mt-4 mb-2 col-span-1 md:col-span-2"}>Govern Unstructured Data</h3>
                            <div className={gridClasses}>
                                <div className="md:col-span-1">
                                    <label htmlFor="unstructured-hours" className={labelClasses}>Hours per Month Spent on Unstructured Data Gov.</label>
                                    <input type="number" id="unstructured-hours" className={inputClasses} value={inputs.input_unstructured_data || ''} onChange={handleNumberInput('input_unstructured_data')} min={0} />
                                    <p className="text-xs text-gray-500 mt-1 col-span-1 md:col-span-2">Benchmark: Manual process requires ~100 hours/month per 1000 high-access employees.</p>
                                </div>
                                <div className="hidden md:block"></div>
                            </div>
                        </div>
                    )}

                </div>
            </section>
        </div>
    );
};

export default InputForm;
